import React from 'react';
import './SharedGameEnrichment.css';

// NBA Watch Button Component - Premium cross-platform experience
const NBAWatchButton = ({ nbaWatchUrl, gameDate, isCompact = false, className = "" }) => {
    if (!nbaWatchUrl) return null;
    
    // Determine if this is a recent game (more likely to be available)
    const isRecentGame = () => {
        if (!gameDate) return true; // Default to showing button
        
        const gameDateTime = new Date(gameDate);
        const now = new Date();
        const daysDiff = (now - gameDateTime) / (1000 * 60 * 60 * 24);
        
        // Show for games within last 20 years (NBA League Pass has extensive historical archives)
        return daysDiff <= 7300; // 20 years * 365 days
    };
    
        // Smart click handler for cross-platform behavior
    const handleWatchClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // For mobile devices, use the most reliable approach
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (isMobile) {
            // Extract game ID for potential deep link
            const gameIdMatch = nbaWatchUrl.match(/-(\d{10})$/);
            const gameId = gameIdMatch ? gameIdMatch[1] : null;
            
            if (gameId) {
                // Smart deep-linking strategy for mobile
                // Use a progressive enhancement approach
                
                try {
                    // Method 1: Use the NBA.com Universal Link (iOS) / Intent URL (Android)
                    // This is the most reliable method that avoids security warnings
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    const isAndroid = /Android/.test(navigator.userAgent);
                    
                    if (isIOS) {
                        // For iOS: Try Universal Link first, then fallback
                        // Universal Links work seamlessly without security warnings
                        const universalLinkAttempted = localStorage.getItem('nba_universal_link_attempted');
                        
                        if (!universalLinkAttempted) {
                            // First time - try Universal Link
                            localStorage.setItem('nba_universal_link_attempted', 'true');
                            window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
                            
                            // If user comes back quickly, Universal Link didn't work
                            // Set up fallback for next click
                            setTimeout(() => {
                                localStorage.setItem('nba_universal_link_failed', 'true');
                            }, 2000);
                        } else if (localStorage.getItem('nba_universal_link_failed')) {
                            // Universal Link failed before, try custom scheme with user consent
                            const tryApp = window.confirm('Open in NBA app?');
                            if (tryApp) {
                                // Use hidden iframe method (less aggressive)
                                const iframe = document.createElement('iframe');
                                iframe.style.display = 'none';
                                iframe.src = `nba://game/${gameId}`;
                                document.body.appendChild(iframe);
                                
                                setTimeout(() => {
                                    document.body.removeChild(iframe);
                                    // Fallback to web if app doesn't open
                                    window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
                                }, 1000);
                            } else {
                                window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
                            }
                        } else {
                            // Try Universal Link again
                            window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
                        }
                    } else if (isAndroid) {
                        // For Android: Use Intent URL which is more reliable
                        const intentUrl = `intent://game/${gameId}#Intent;scheme=nba;package=com.nba.gamenext;S.browser_fallback_url=${encodeURIComponent(nbaWatchUrl)};end`;
                        window.location.href = intentUrl;
                    } else {
                        // Other mobile browsers - just use web version
                        window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
                    }
                    
                } catch (error) {
                    // If everything fails, just open web version
                    window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
                }
            } else {
                // No game ID - just open web version
                window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
            }
        } else {
            // Desktop - always open web version
            window.open(nbaWatchUrl, '_blank', 'noopener,noreferrer');
        }
        
        // Analytics tracking (if available)
        if (window.gtag) {
            window.gtag('event', 'nba_watch_click', {
                'game_url': nbaWatchUrl,
                'platform': isMobile ? 'mobile' : 'desktop'
            });
        }
    };
    
    if (!isRecentGame()) {
        return null; // Don't show button for very old games
    }
    
    return (
        <a 
            href={nbaWatchUrl}
            onClick={handleWatchClick}
            className={`nba-watch-btn ${isCompact ? 'compact' : ''} ${className}`}
            title="Watch this game on NBA League Pass"
            target="_blank"
            rel="noopener noreferrer"
        >
            <img 
                src="https://cdn.nba.com/logos/leagues/L/logo-nba.svg" 
                alt="NBA"
                className="nba-logo-img"
            />
            <span className="watch-text">
                {isCompact ? 'Watch on NBA' : 'Watch on NBA League Pass'}
            </span>
            <span className="external-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                </svg>
            </span>
        </a>
    );
};

/**
 * SharedGameEnrichment - Modern shared components for co-star level enrichment
 * 
 * Based on the successful DualPlayerTheaterCard patterns, these components provide
 * consistent rich game information across all card types.
 */

// Player Visual Header Component - Shows player photo with game context
const PlayerVisualHeader = ({ playerVisuals, game, className = "" }) => {
    if (!playerVisuals) return null;

    const photoUrl = playerVisuals.photo_url || playerVisuals.cutout_url;
    if (!photoUrl) return null;

    return (
        <div className={`player-visual-header ${className}`}>
            <div className="player-photo-container">
                <img 
                    src={photoUrl} 
                    alt={`${playerVisuals.player_name} photo`}
                    className="player-photo"
                    onError={(e) => {
                        e.target.style.display = 'none';
                    }}
                />
                <div className="player-overlay">
                    <div className="player-name">{playerVisuals.player_name}</div>
                    {playerVisuals.position && (
                        <div className="player-position">{playerVisuals.position}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Team Details Component - Shows lineups, top scorers, and team info
const TeamDetailsSection = ({ team, teamType, compact = false, teamVisuals = null }) => {
    if (!team) return null;

    // Use visual data if available, fallback to team data
    const logoUrl = teamVisuals?.logo_url || team.logo;
    const teamName = teamVisuals?.full_name || team.name || `${teamType} Team`;
    const primaryColor = teamVisuals?.primary_color;

    return (
        <div className={`team-details ${compact ? 'compact' : ''}`}>
            <div className="team-header" style={primaryColor ? { borderLeftColor: primaryColor } : {}}>
                {logoUrl && (
                    <img 
                        src={logoUrl} 
                        alt={`${teamName} logo`}
                        className="team-logo"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                )}
                <div className="team-name-container">
                    <h6 className="team-name" style={primaryColor ? { color: primaryColor } : {}}>
                        {teamName}
                    </h6>
                    {teamVisuals?.secondary_color && (
                        <div 
                            className="team-color-accent" 
                            style={{ backgroundColor: teamVisuals.secondary_color }}
                        />
                    )}
                </div>
            </div>
            
            {/* Starting Lineup */}
            {team.starting_lineup && team.starting_lineup.length > 0 && (
                <div className="lineup-section">
                    <div className="section-title">Starting Lineup</div>
                    <div className={`lineup-grid ${compact ? 'compact' : ''}`}>
                        {team.starting_lineup.slice(0, 6).map((player, idx) => (
                            <div key={idx} className={`lineup-player ${player.is_sixth_man ? 'sixth-man' : ''}`}>
                                <div className="player-name">
                                    {player.player_name}
                                    {player.is_sixth_man && <span className="sixth-man-badge">6TH</span>}
                                </div>
                                <div className="player-position">{player.position}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Top Scorers */}
            {team.top_scorers && team.top_scorers.length > 0 && (
                <div className="scorers-section">
                    <div className="section-title">Top Scorers</div>
                    <div className="scorers-list">
                        {team.top_scorers.slice(0, 3).map((player, idx) => (
                            <div key={idx} className="scorer-player">
                                <div className="scorer-rank">#{idx + 1}</div>
                                <div className="scorer-info">
                                    <div className="scorer-name">{player.player_name}</div>
                                    <div className="scorer-stats">
                                        {player.points}PTS • {player.rebounds}REB • {player.assists}AST
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Game Competitiveness Component - Shows game quality metrics
const CompetitivenessSection = ({ competitiveness }) => {
    if (!competitiveness) return null;

    return (
        <div className="competitiveness-section">
            <div className="competitiveness-header">
                <span className="competitiveness-icon">🎯</span>
                <span className="competitiveness-title">Game Quality</span>
            </div>
            
            <div className="competitiveness-content">
                <div className="competitiveness-score">
                    <div className="score-display">
                        <span className="score-number">{competitiveness.competitiveness_score}</span>
                        <span className="score-max">/10</span>
                    </div>
                    <div className="score-label">Competitiveness</div>
                </div>
                
                <div className="competitiveness-details">
                    {competitiveness.recommendation && (
                        <div className="recommendation-badge">
                            {competitiveness.recommendation}
                        </div>
                    )}
                    
                    {competitiveness.analysis && (
                        <div className="analysis-text">
                            {competitiveness.analysis}
                        </div>
                    )}
                    
                    {competitiveness.excitement_indicators && (
                        <div className="excitement-indicators">
                            {competitiveness.excitement_indicators.map((indicator, idx) => (
                                <span key={idx} className="indicator-tag">
                                    {indicator}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main Game Details Component - Combines teams and competitiveness
const GameDetailsSection = ({ 
    game_details, 
    compact = false, 
    showCompetitiveness = true,
    homeTeamFirst = true,
    teamVisuals = []
}) => {
    if (!game_details) return null;
    
    // Check if we have meaningful enrichment data
    const hasHomeData = game_details.home_team?.starting_lineup || game_details.home_team?.top_scorers;
    const hasAwayData = game_details.away_team?.starting_lineup || game_details.away_team?.top_scorers;
    
    if (!hasHomeData && !hasAwayData) {
        return null; // No enrichment data available
    }

    // Map team visuals by team name
    const getTeamVisuals = (teamName) => {
        if (!teamVisuals || !Array.isArray(teamVisuals)) return null;
        return teamVisuals.find(tv => 
            tv?.team_name === teamName || 
            tv?.full_name === teamName ||
            teamName?.includes(tv?.team_name)
        );
    };

    const homeTeamVisuals = getTeamVisuals(game_details.home_team?.name);
    const awayTeamVisuals = getTeamVisuals(game_details.away_team?.name);

    return (
        <div className="game-details-section">
            <div className="game-details-header">
                <span className="details-icon">🏀</span>
                <span className="details-title">Game Details</span>
            </div>
            
            <div className="teams-details">
                {homeTeamFirst ? (
                    <>
                        <TeamDetailsSection 
                            team={game_details.home_team} 
                            teamType="Home" 
                            compact={compact}
                            teamVisuals={homeTeamVisuals}
                        />
                        <TeamDetailsSection 
                            team={game_details.away_team} 
                            teamType="Away" 
                            compact={compact}
                            teamVisuals={awayTeamVisuals}
                        />
                    </>
                ) : (
                    <>
                        <TeamDetailsSection 
                            team={game_details.away_team} 
                            teamType="Away" 
                            compact={compact}
                            teamVisuals={awayTeamVisuals}
                        />
                        <TeamDetailsSection 
                            team={game_details.home_team} 
                            teamType="Home" 
                            compact={compact}
                            teamVisuals={homeTeamVisuals}
                        />
                    </>
                )}
            </div>

            {/* Game Competitiveness Section */}
            {showCompetitiveness && (
                <CompetitivenessSection competitiveness={game_details.competitiveness} />
            )}
        </div>
    );
};

// Enhanced Card Components for each mode

// Enhanced Must-Watch Card
const EnrichedMustWatchCard = ({ game }) => {
    const enhancement = game.enhancement || {};
    const achievements = enhancement.achievements || [];
    
    const watchabilityTier = enhancement.watchability_tier || 'watchable';
    const watchabilityScore = enhancement.watchability_score || 0;
    const performanceGrade = enhancement.performance_grade || 'C';

    const getTierDisplay = (tier) => {
        const tierMap = {
            'must_watch': { label: 'Must Watch', color: '#ff453a' },
            'excellent': { label: 'Excellent', color: '#ff9500' },
            'very_good': { label: 'Very Good', color: '#0a84ff' }
        };
        return tierMap[tier] || tierMap['excellent'];
    };

    const tierDisplay = getTierDisplay(watchabilityTier);

    return (
        <div className={`enriched-must-watch-card ${watchabilityTier}`}>
            <div className="must-watch-header">
                <div className="game-info">
                    <div className="game-date">{game.game_date}</div>
                    <div className="matchup">{game.matchup}</div>
                </div>
                <div className="must-watch-tier" style={{ color: tierDisplay.color }}>
                    {tierDisplay.label}
                </div>
            </div>

            <div className="performance-highlight">
                <div className="stats-showcase">
                    <div className="main-stat">
                        <span className="stat-number">{game.pts}</span>
                        <span className="stat-type">PTS</span>
                    </div>
                    <div className="supporting-stats">
                        <div className="stat-item">
                            <span className="stat-value">{game.reb}</span>
                            <span className="stat-label">REB</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-value">{game.ast}</span>
                            <span className="stat-label">AST</span>
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="grade-section">
                        <span className="grade-text">Grade</span>
                        <span className={`grade-badge grade-${performanceGrade.toLowerCase()}`}>
                            {performanceGrade}
                        </span>
                    </div>
                </div>
            </div>

            {achievements.length > 0 && (
                <div className="achievements-showcase">
                    <h4>Notable Achievements</h4>
                    <div className="achievements-list">
                        {achievements.slice(0, 2).map((achievement, idx) => (
                            <span key={idx} className={`achievement-badge ${achievement.rarity}`}>
                                {achievement.description}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {enhancement.spoiler_free_description && (
                <div className="game-description">
                    <p>{enhancement.spoiler_free_description}</p>
                </div>
            )}

            {/* Co-star level enrichment */}
            <GameDetailsSection 
                game_details={game.game_details} 
                compact={true} 
                showCompetitiveness={true}
                teamVisuals={game.team_visuals}
            />

            {/* NBA Watch Button */}
            <NBAWatchButton 
                nbaWatchUrl={game.nba_watch_url} 
                gameDate={game.game_date}
                isCompact={false}
                className="nba-watch-btn must-watch-btn"
            />
        </div>
    );
};

// Helper functions for enhanced elite player tags
const getElitePlayerTag = (star) => {
    const status = star.status || 'prime';
    const stats = star.stats || {};
    const pts = stats.pts || 0;
    const ast = stats.ast || 0;
    const reb = stats.reb || 0;
    
    // Career stage tags with performance context
    if (status === 'prime') {
        if (pts >= 28) return '🔥 Superstar';
        if (pts >= 25) return '⭐ All-Star';
        if (ast >= 8) return '🎯 Floor General';
        if (reb >= 10) return '💪 Dominant';
        return '🔥 Prime';
    } else if (status === 'emerging') {
        if (pts >= 22) return '🚀 Rising Star';
        if (pts >= 18) return '📈 Breakout';
        if (ast >= 6) return '🎯 Young Gun';
        return '📈 Emerging';
    } else if (status === 'veteran') {
        if (pts >= 20) return '👑 Veteran Star';
        if (ast >= 7) return '🧠 Court Veteran';
        return '👑 Veteran';
    }
    
    return '⭐ Elite';
};

const getEliteCategoryTag = (star) => {
    const reasons = star.elite_reasons || [];
    const overallStatus = star.overall_status || 'star';
    const stats = star.stats || {};
    const pts = stats.pts || 0;
    const ast = stats.ast || 0;
    const reb = stats.reb || 0;
    
    // Multi-category elite players
    if (overallStatus === 'multi_category_elite') {
        if (reasons.includes('scoring_leader') && reasons.includes('playmaking_leader')) {
            return '🎭 Dual Threat';
        }
        if (reasons.includes('scoring_leader') && reasons.includes('rebounding_leader')) {
            return '⚔️ Two-Way Force';
        }
        if (reasons.includes('playmaking_leader') && reasons.includes('rebounding_leader')) {
            return '🔄 Complete Player';
        }
        return '💎 Multi-Elite';
    }
    
    // Specialized elite categories with performance context
    if (overallStatus === 'offensive_star') {
        if (pts >= 30) return '🔥 Scoring Machine';
        if (pts >= 27) return '🎯 Elite Scorer';
        if (pts >= 24) return '⚡ Bucket Getter';
        return '🏀 Offensive Star';
    }
    
    if (overallStatus === 'playmaker') {
        if (ast >= 9) return '⚡ Maestro';
        if (ast >= 6) return '🎮 Distributor';
        return '🎯 Playmaker';
    }
    
    if (overallStatus === 'rebounder') {
        if (reb >= 12) return '💪 Board Beast';
        if (reb >= 10) return '🔄 Glass Cleaner';
        if (reb >= 8) return '💪 Rebounder';
        return '🏀 Hustle Player';
    }
    
    if (overallStatus === 'specialist') {
        if (reasons.includes('efficiency_leader')) return '🎯 Efficient';
        return '⚡ Specialist';
    }
    
    return '⭐ Elite Player';
};

const getTagTooltip = (tag) => {
    const tooltipMap = {
        // Matchup Tiers
        'Legendary': 'Epic battles between superstars with major storylines',
        'Compelling': 'High-quality matchups with notable players and context',
        'Notable': 'Solid games with interesting elements',
        'Watchable': 'Standard games worth watching',
        
        // Career Stages
        'Superstar': 'Elite players at the peak of their powers (28+ PPG)',
        'All-Star': 'Top-tier players with all-star caliber performance (25+ PPG)',
        'Rising Star': 'Young players breaking out (22+ PPG emerging players)',
        'Veteran Star': 'Experienced players still performing at high level (20+ PPG veterans)',
        'Court Veteran': 'High basketball IQ veterans (7+ APG)',
        'Floor General': 'Elite playmakers leading their teams (8+ APG)',
        
        // Elite Categories
        'Dual Threat': 'Players excelling in both scoring and playmaking',
        'Two-Way Force': 'Players dominating scoring and rebounding',
        'Complete Player': 'Players contributing in playmaking and rebounding',
        'Scoring Machine': 'Elite scorers putting up 30+ PPG',
        '⚡ Maestro': 'Master playmakers with 9+ assists per game',
        'Maestro': 'Master playmakers with 9+ assists per game',
        '🎮 Distributor': 'Solid facilitators with 6+ assists per game',
        'Distributor': 'Solid facilitators with 6+ assists per game',
        'Board Beast': 'Dominant rebounders with 12+ rebounds per game',
        
        // Story Types
        'Rivalry': 'Historic team rivalries and heated matchups',
        'Homecoming': 'Players facing their former teams',
        'Milestone': 'Games featuring 50+ point performances or triple-doubles',
        'Playoff Race': 'Late season games with playoff implications',
        'Draft Class': 'Battles between players from the same draft year',
        
        // Performance Context
        'Breakout': 'Emerging players having breakthrough performances (18+ PPG)',
        'Prime': 'Players in their peak years',
        'Emerging': 'Young players establishing themselves',
        'Veteran': 'Experienced players with championship experience'
    };
    
    return tooltipMap[tag] || `${tag} - Special game classification`;
};

// Enhanced Legendary Matchup Card
const EnrichedLegendaryMatchupCard = ({ game }) => {
    const enhancement = game.enhancement || {};
    const matchupAnalysis = game.matchup_analysis || {};
    const opponentStars = game.opponent_stars || [];
    const playerPerformance = game.player_performance || {};
    
    // Provide default values
    const watchabilityTier = enhancement.watchability_tier || 'watchable';
    const watchabilityScore = enhancement.watchability_score || 0;
    const performanceGrade = enhancement.performance_grade || 'C';
    const achievements = enhancement.achievements || [];
    
    // Matchup specific data
    const matchupTier = matchupAnalysis.matchup_tier || 'standard';
    const excitementScore = matchupAnalysis.excitement_score || 0;
    const description = matchupAnalysis.description || '';
    const narrativeElements = matchupAnalysis.narrative_elements || [];
    
    // Player stats from performance analysis
    const stats = playerPerformance.stats || {};
    const performanceLevel = playerPerformance.performance_level || 'decent';

    const getTierDisplay = (tier) => {
        const tierMap = {
            'legendary': { emoji: '🏆', label: 'LEGENDARY', color: '#ff6b6b' },
            'compelling': { emoji: '⚡', label: 'COMPELLING', color: '#4ecdc4' },
            'notable': { emoji: '⭐', label: 'NOTABLE', color: '#667eea' },
            'standard': { emoji: '👀', label: 'STANDARD', color: '#8e8e93' }
        };
        return tierMap[tier] || tierMap['standard'];
    };

    const tierDisplay = getTierDisplay(matchupTier);

    return (
        <div className={`enriched-legendary-matchup-card ${matchupTier}`}>
            <div className="matchup-header">
                <div className="game-info">
                    <div className="game-date">{game.game_date}</div>
                    <div className="matchup">{game.matchup}</div>
                </div>
                <div className="matchup-tier" style={{ color: tierDisplay.color }}>
                    {tierDisplay.emoji} {tierDisplay.label}
                </div>
            </div>

            <div className="matchup-narrative">
                <h4>🎭 The Battle</h4>
                <p>{description}</p>
                {narrativeElements.length > 0 && (
                    <div className="narrative-elements">
                        {narrativeElements.map((element, idx) => (
                            <span key={idx} className="narrative-tag">
                                {element}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {opponentStars.length > 0 && (
                <div className="opponent-stars">
                    <h4>⭐ Elite Opponents</h4>
                    <div className="stars-list">
                        {opponentStars.map((star, idx) => (
                            <div key={idx} className="star-player">
                                <span className="star-name">{star.name}</span>
                                <div className="star-tags">
                                    <span className={`star-status ${star.status || 'prime'}`}>
                                        {getElitePlayerTag(star)}
                                    </span>
                                    <span className={`elite-category ${star.overall_status || 'star'}`}>
                                        {getEliteCategoryTag(star)}
                                    </span>
                                </div>
                                <span className="star-position">{star.position}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filter Tags Section - Show relevant tags for this matchup */}
            {(game.matchup_tags || game.filter_tags || game.tags) && (
                <div className="matchup-tags">
                    {(game.matchup_tags || game.filter_tags || game.tags || []).map((tag, idx) => (
                        <span 
                            key={idx} 
                            className={`matchup-tag ${tag.toLowerCase().replace(/\s+/g, '-')}`}
                            title={getTagTooltip(tag)}
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            )}

            <div className="performance-breakdown">
                <div className="player-stats">
                    <h4>📊 Performance</h4>
                    <div className="stat-line">
                        <span className="pts">{stats.pts || game.pts} PTS</span>
                        <span className="reb">{stats.reb || game.reb} REB</span>
                        <span className="ast">{stats.ast || game.ast} AST</span>
                        {(stats.stl || game.stl) > 0 && <span className="stl">{stats.stl || game.stl} STL</span>}
                        {(stats.blk || game.blk) > 0 && <span className="blk">{stats.blk || game.blk} BLK</span>}
                    </div>
                    <div className="performance-level">
                        Level: <span className={`level-${performanceLevel}`}>
                            {performanceLevel.toUpperCase()}
                        </span>
                    </div>
                </div>
                
                <div className="excitement-meter">
                    <h4>🔥 Excitement Score</h4>
                    <div className="score-display">
                        <span className="score-number">{excitementScore}</span>
                        <span className="score-max">/10</span>
                    </div>
                </div>
            </div>

            {achievements.length > 0 && (
                <div className="achievements">
                    <h4>🏆 Achievements</h4>
                    {achievements.map((achievement, idx) => (
                        <span key={idx} className={`achievement ${achievement.rarity}`}>
                            {achievement.icon} {achievement.description}
                        </span>
                    ))}
                </div>
            )}

            {/* Co-star level enrichment */}
            <GameDetailsSection 
                game_details={game.game_details} 
                compact={true} 
                showCompetitiveness={true}
                teamVisuals={game.team_visuals}
            />

            {/* NBA Watch Button */}
            <NBAWatchButton 
                nbaWatchUrl={game.nba_watch_url} 
                gameDate={game.game_date}
                isCompact={false}
                className="nba-watch-btn must-watch-btn"
            />
        </div>
    );
};

// Enhanced Discovery Mode Card V2 - With Context
const EnrichedDiscoveryModeCard = ({ game, modeType, discoveryContext }) => {
    const enhancement = game.enhancement || {};
    const achievements = enhancement.achievements || [];
    const context = game.discovery_context || discoveryContext || {};
    
    const watchabilityTier = enhancement.watchability_tier || 'watchable';
    const watchabilityScore = enhancement.watchability_score || 0;
    const performanceGrade = enhancement.performance_grade || 'C';

    const getModeDisplay = (mode) => {
        const modeMap = {
            'must_watch': { label: 'Classic', color: '#ff453a' },
            'high_scoring': { label: 'Scoring', color: '#ff9500' },
            'playmaking': { label: 'Playmaking', color: '#30d158' },
            'competitive': { label: 'Competitive', color: '#0a84ff' }
        };
        return modeMap[mode] || { label: 'Featured', color: '#8e8e93' };
    };

    const modeDisplay = getModeDisplay(modeType);

    const getWatchabilityDisplay = (tier) => {
        const tierMap = {
            'must_watch': { label: 'Must Watch', color: '#ff453a' },
            'excellent': { label: 'Excellent', color: '#ff9500' },
            'very_good': { label: 'Very Good', color: '#0a84ff' },
            'good': { label: 'Good', color: '#30d158' },
            'watchable': { label: 'Watchable', color: '#8e8e93' }
        };
        return tierMap[tier] || tierMap['watchable'];
    };

    const watchabilityDisplay = getWatchabilityDisplay(watchabilityTier);

    return (
        <div className={`enriched-discovery-card ${watchabilityTier}`}>
            <div className="discovery-card-header">
                <div className="game-meta">
                    <div className="game-date">{game.game_date}</div>
                    <div className="matchup-text">{game.matchup}</div>
                </div>
                <div className="tier-indicator" style={{ color: watchabilityDisplay.color }}>
                    {watchabilityDisplay.label}
                </div>
            </div>

            <div className="stats-showcase">
                <div className="main-stat">
                    <span className="stat-number">{game.pts}</span>
                    <span className="stat-type">PTS</span>
                </div>
                <div className="supporting-stats">
                    <div className="stat-item">
                        <span className="stat-value">{game.reb}</span>
                        <span className="stat-label">REB</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{game.ast}</span>
                        <span className="stat-label">AST</span>
                    </div>
                </div>
            </div>

            {/* Context Description */}
            {context.description && (
                <div className="discovery-context">
                    <div className="context-description">{context.description}</div>
                    {context.elite_opponents && context.elite_opponents.length > 0 && (
                        <div className="elite-opponents">
                            vs {context.elite_opponents.join(', ')}
                        </div>
                    )}
                </div>
            )}

            <div className="card-footer">
                <div className="grade-section">
                    <span className="grade-text">Grade</span>
                    <span className={`grade-badge grade-${performanceGrade.toLowerCase()}`}>
                        {performanceGrade}
                    </span>
                </div>
                <div className="mode-type" style={{ color: modeDisplay.color }}>
                    {modeDisplay.label}
                </div>
            </div>

            {achievements.length > 0 && (
                <div className="notable-achievements">
                    {achievements.slice(0, 1).map((achievement, idx) => (
                        <span key={idx} className={`achievement-highlight ${achievement.rarity}`}>
                            {achievement.description}
                        </span>
                    ))}
                </div>
            )}

            {/* Co-star level enrichment */}
            <GameDetailsSection 
                game_details={game.game_details} 
                compact={true} 
                showCompetitiveness={false}
                teamVisuals={game.team_visuals}
            />

            {/* NBA Watch Button */}
            <NBAWatchButton 
                nbaWatchUrl={game.nba_watch_url} 
                gameDate={game.game_date}
                isCompact={false}
                className="nba-watch-btn must-watch-btn"
            />
        </div>
    );
};

// Enhanced All Games Card
const EnrichedGameCard = ({ game, isHighlight = false }) => {
    const enhancement = game.enhancement || {};
    const achievements = enhancement.achievements || [];
    
    // Provide default values for missing enhancement data
    const watchabilityTier = enhancement.watchability_tier || 'watchable';
    const watchabilityScore = enhancement.watchability_score || 0;
    const performanceGrade = enhancement.performance_grade || 'C';

    const getGameQuality = (tier, grade) => {
        // Apple approach: ONE clear, consistent indicator
        // Priority: watchability tier > grade (watchability is more consumer-friendly)
        const qualityMap = {
            'must_watch': { emoji: '🔥', label: 'Must Watch', color: '#ff453a' },
            'excellent': { emoji: '⭐', label: 'Excellent', color: '#ff9500' },
            'very_good': { emoji: '👍', label: 'Good Game', color: '#0a84ff' },
            'good': { emoji: '👍', label: 'Good Game', color: '#30d158' }
        };

        const quality = qualityMap[tier];
        
        // Only show for meaningful games
        if (!quality) return null;

        return (
            <div className="game-quality" style={{ color: quality.color }}>
                <span className="quality-emoji">{quality.emoji}</span>
                <span className="quality-text">{quality.label}</span>
            </div>
        );
    };

    return (
        <div className={`enriched-game-card ${isHighlight ? 'highlight' : ''} ${watchabilityTier} ${game.is_playoff ? 'playoff-game' : ''}`}>
            <div className="game-header">
                <div className="game-info">
                    <div className="game-date">
                        {game.game_date}
                        {game.is_playoff && <span className="playoff-badge">PLAYOFFS</span>}
                    </div>
                    <div className="matchup">{game.matchup}</div>
                </div>
                {getGameQuality(watchabilityTier, performanceGrade)}
            </div>

            <div className="game-stats">
                <div className="stat-line">
                    <span className="pts">{game.pts} PTS</span>
                    <span className="reb">{game.reb} REB</span>
                    <span className="ast">{game.ast} AST</span>
                    {game.stl > 0 && <span className="stl">{game.stl} STL</span>}
                    {game.blk > 0 && <span className="blk">{game.blk} BLK</span>}
                </div>
            </div>

            {achievements.length > 0 && (
                <div className="achievements">
                    {achievements.map((achievement, idx) => (
                        <span key={idx} className={`achievement ${achievement.rarity}`}>
                            {achievement.icon} {achievement.description}
                        </span>
                    ))}
                </div>
            )}

            {enhancement.spoiler_free_description && (
                <div className="spoiler-free-description">
                    {enhancement.spoiler_free_description}
                </div>
            )}

            {/* Co-star level enrichment */}
            <GameDetailsSection 
                game_details={game.game_details} 
                compact={false} 
                showCompetitiveness={true}
                teamVisuals={game.team_visuals}
            />

            {/* NBA Watch Button */}
            <NBAWatchButton 
                nbaWatchUrl={game.nba_watch_url} 
                gameDate={game.game_date}
                isCompact={false}
                className="nba-watch-btn must-watch-btn"
            />
        </div>
    );
};

// Export all components
export {
    PlayerVisualHeader,
    GameDetailsSection,
    TeamDetailsSection,
    CompetitivenessSection,
    EnrichedMustWatchCard,
    EnrichedLegendaryMatchupCard,
    EnrichedDiscoveryModeCard,
    EnrichedGameCard
};

export default GameDetailsSection;
